/**
 * GroupJoinPage
 *
 * Handles join-by-link flow for Group Prioritization sessions.
 * Route: /groups/join/:token  (public — no RequireAuth wrapper)
 *
 * Two scenarios:
 *  A. User is already authenticated → resolve token immediately → redirect to session
 *  B. User is not authenticated → show inline login form with session context →
 *     after login, resolve token → redirect to session
 *
 * This mirrors the SpaceInviteAccept pattern so participants never land on the
 * generic login page with no context about what they're joining.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGroupSessionStore } from '../../stores/prioritization/groupSessionStore';
import { getSupabaseClient } from '../../lib/supabaseClient';

// ─── Types ────────────────────────────────────────────────────────────────────

type PageState = 'checking-auth' | 'login-required' | 'resolving' | 'redirecting' | 'error';

// ─── Shared style tokens (matches GroupSessionPage / GroupSetupView) ──────────

const sans = 'var(--sans)';
const serif = 'var(--serif)';
const mono = 'var(--mono)';

// ─── Sub-components ───────────────────────────────────────────────────────────

function Spinner({ color = 'var(--mustard)' }: { color?: string }) {
  return (
    <>
      <style>{`@keyframes pp-spin { to { transform: rotate(360deg); } }`}</style>
      <div
        style={{
          width: '44px',
          height: '44px',
          border: '3px solid var(--rule-bright)',
          borderTopColor: color,
          borderRadius: '50%',
          animation: 'pp-spin 0.9s linear infinite',
        }}
      />
    </>
  );
}

function Wordmark() {
  return (
    <div
      style={{
        fontFamily: serif,
        fontSize: '20px',
        fontWeight: 600,
        color: 'var(--mustard)',
        marginBottom: '40px',
        letterSpacing: '-0.01em',
      }}
    >
      Peak Priorities
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function GroupJoinPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { loadGroupSessionByToken } = useGroupSessionStore();

  const [pageState, setPageState] = useState<PageState>('checking-auth');
  const [errorMessage, setErrorMessage] = useState('');

  // ── Login form state ────────────────────────────────────────────────────────
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  // ── Resolve token and navigate to session ───────────────────────────────────
  const resolveAndRedirect = useCallback(async () => {
    if (!token) {
      setErrorMessage('No join token provided.');
      setPageState('error');
      return;
    }

    setPageState('resolving');

    try {
      const resolvedSessionId = await loadGroupSessionByToken(token);

      if (!resolvedSessionId) {
        setErrorMessage("This link isn't valid or has expired. Ask the host for a new invite link.");
        setPageState('error');
        return;
      }

      setPageState('redirecting');
      navigate(`/groups/${resolvedSessionId}`, { replace: true });
    } catch (err) {
      console.error('[GroupJoinPage] Failed to resolve token:', err);
      setErrorMessage(
        'Something went wrong while loading this invite link. Please try again or ask the host for a new one.',
      );
      setPageState('error');
    }
  }, [token, loadGroupSessionByToken, navigate]);

  // ── On mount: check auth state ──────────────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setErrorMessage('No join token provided.');
      setPageState('error');
      return;
    }

    async function init() {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        // Already authenticated — resolve immediately
        await resolveAndRedirect();
      } else {
        // Not authenticated — show login form with context
        setPageState('login-required');
      }
    }

    void init();
  }, [token, resolveAndRedirect]);

  // ── Login handler ───────────────────────────────────────────────────────────
  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError('');

    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (error) {
      setLoginError(error.message);
      setLoggingIn(false);
      return;
    }

    // Signed in — now resolve the token
    await resolveAndRedirect();
    setLoggingIn(false);
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <div style={{ textAlign: 'center' }}>
          <Wordmark />
        </div>

        {/* Checking auth */}
        {pageState === 'checking-auth' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <Spinner />
            <p style={{ fontFamily: sans, fontSize: '15px', color: 'var(--text-sec)', margin: 0 }}>
              Loading…
            </p>
          </div>
        )}

        {/* Resolving token */}
        {pageState === 'resolving' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <Spinner />
            <p style={{ fontFamily: sans, fontSize: '15px', color: 'var(--text-sec)', margin: 0 }}>
              Joining session…
            </p>
          </div>
        )}

        {/* Redirecting */}
        {pageState === 'redirecting' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <Spinner color="var(--green)" />
            <p className="dark:!text-[var(--text-ter)]" style={{ fontFamily: sans, fontSize: '15px', color: 'var(--green)', margin: 0 }}>
              Session found — taking you there…
            </p>
          </div>
        )}

        {/* Login required */}
        {pageState === 'login-required' && (
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--rule-bright)',
              borderRadius: '16px',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '24px 28px 20px',
                borderBottom: '1px solid var(--rule)',
              }}
            >
              <p
                style={{
                  fontFamily: mono,
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--mustard)',
                  margin: '0 0 6px',
                }}
              >
                You're invited
              </p>
              <h1
                style={{
                  fontFamily: serif,
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--text-pri)',
                  margin: '0 0 6px',
                  lineHeight: 1.3,
                }}
              >
                Join a group decision
              </h1>
              <p
                style={{
                  fontFamily: sans,
                  fontSize: '13px',
                  color: 'var(--text-sec)',
                  margin: 0,
                  lineHeight: 1.5,
                }}
              >
                Sign in to join this session and add your scores.
              </p>
            </div>

            {/* Login form */}
            <form onSubmit={handleLogin} style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label
                  htmlFor="gjp-email"
                  style={{ fontFamily: sans, fontSize: '13px', fontWeight: 500, color: 'var(--text-sec)', display: 'block', marginBottom: '6px' }}
                >
                  Email
                </label>
                <input
                  id="gjp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    fontFamily: sans,
                    fontSize: '14px',
                    color: 'var(--text-pri)',
                    background: 'var(--bg)',
                    border: '1px solid var(--rule-bright)',
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label
                  htmlFor="gjp-password"
                  style={{ fontFamily: sans, fontSize: '13px', fontWeight: 500, color: 'var(--text-sec)', display: 'block', marginBottom: '6px' }}
                >
                  Password
                </label>
                <input
                  id="gjp-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Your password"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    fontFamily: sans,
                    fontSize: '14px',
                    color: 'var(--text-pri)',
                    background: 'var(--bg)',
                    border: '1px solid var(--rule-bright)',
                    borderRadius: '8px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {loginError && (
                <div
                  style={{
                    background: 'var(--coral-dim)',
                    border: '1px solid var(--coral)',
                    borderRadius: '8px',
                    padding: '10px 14px',
                    fontFamily: sans,
                    fontSize: '13px',
                    color: 'var(--coral)',
                    lineHeight: 1.5,
                  }}
                >
                  {loginError}
                </div>
              )}

              <button
                type="submit"
                disabled={loggingIn}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontFamily: sans,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--bg)',
                  background: loggingIn ? 'var(--rule-bright)' : 'var(--mustard)',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: loggingIn ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                {loggingIn ? 'Signing in…' : 'Sign in & join session'}
              </button>

              <p
                style={{
                  fontFamily: sans,
                  fontSize: '12px',
                  color: 'var(--text-ter)',
                  margin: 0,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Don't have an account?{' '}
                <a
                  href="/auth/login"
                  style={{ color: 'var(--mustard)', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                >
                  Contact the host for access.
                </a>
              </p>
            </form>
          </div>
        )}

        {/* Error */}
        {pageState === 'error' && (
          <div
            style={{
              background: 'var(--coral-dim)',
              border: '1px solid var(--coral)',
              borderRadius: '12px',
              padding: '28px 24px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '16px' }}>✕</div>
            <h1
              style={{
                fontFamily: serif,
                fontSize: '18px',
                fontWeight: 600,
                color: 'var(--coral)',
                margin: '0 0 12px',
                lineHeight: 1.3,
              }}
            >
              Link not valid
            </h1>
            <p
              style={{
                fontFamily: sans,
                fontSize: '14px',
                color: 'var(--text-sec)',
                margin: '0 0 20px',
                lineHeight: 1.6,
              }}
            >
              {errorMessage}
            </p>
            <a
              href="/"
              style={{
                fontFamily: sans,
                fontSize: '13px',
                color: 'var(--text-ter)',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              Go to home
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
